import { useState } from "react";
import { toast } from "sonner";
import { formatDate } from "@/lib/date";
import { useTranslation } from "react-i18next";
import { Check, CreditCard, X } from "lucide-react";
import { useLicense } from "@/hooks/useLicense";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { api, ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PLAN_FEATURES = [
  { label: "DORA metrics", free: "Demo only", pro: "Demo only" },
  { label: "Team members", free: "5", pro: "Unlimited" },
  { label: "Jira integration", free: false, pro: true },
  { label: "API keys", free: "1", pro: "Unlimited" },
  { label: "Onboarding checklist", free: true, pro: true },
  { label: "Priority support", free: false, pro: true },
];

function FeatureCell({ value }: { value: boolean | string }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className="h-4 w-4 text-emerald-500" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground" />
    );
  }
  return <span className="text-sm">{value}</span>;
}

export function LicensePage() {
  const { t } = useTranslation();
  const { license, loading, refetch } = useLicense();
  const isAdmin = useIsAdmin();
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trialing, setTrialing] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);
  const [trialAlreadyUsed, setTrialAlreadyUsed] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [managing, setManaging] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);

  const isTrial = license?.status === "trial";
  const isExpired = license?.status === "expired";
  const isPartnerManaged = !!license?.grantor_partner_id;
  const canTrial = isAdmin && (!license || license.plan === "community") && !trialAlreadyUsed;
  const canUpgrade = isAdmin && (isTrial || trialAlreadyUsed);
  const canManage = isAdmin && license?.status === "active" && !!license?.has_stripe_customer;
  const canChangeToAnnual =
    isAdmin && license?.status === "active" && license?.billing_period === "monthly";

  async function handleStartTrial() {
    setTrialing(true);
    setTrialError(null);
    try {
      await api("/api/v2/billing/trial", { method: "POST" });
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) setTrialError(t("license.trialError.emailNotVerified"));
        else if (err.status === 409) {
          setTrialAlreadyUsed(true);
          setTrialError(t("license.trialError.alreadyUsed"));
        } else if (err.status === 422) setTrialError(t("license.trialError.domainNotEligible"));
        else setTrialError(t("license.trialError.generic"));
      } else {
        setTrialError(t("license.trialError.generic"));
      }
    } finally {
      setTrialing(false);
    }
  }

  async function handleManageSubscription() {
    setManaging(true);
    try {
      const { url } = await api<{ url: string }>("/api/v2/billing/portal-session", {
        method: "POST",
      });
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error(t("license.manageError"));
    } finally {
      setManaging(false);
    }
  }

  async function handleInvoice() {
    setInvoicing(true);
    setInvoiceError(null);
    try {
      const { invoice_url } = await api<{ invoice_url: string }>(
        "/api/v2/billing/invoice",
        { method: "POST" },
      );
      setInvoiceUrl(invoice_url);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setInvoiceError(t("license.invoiceError.alreadyActive"));
      } else {
        setInvoiceError(t("license.invoiceError.generic"));
      }
    } finally {
      setInvoicing(false);
    }
  }

  async function handleUpgrade(period: "monthly" | "annual" = billingPeriod) {
    setUpgrading(true);
    setError(null);
    try {
      const { url } = await api<{ url: string }>("/api/v2/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ billing_period: period }),
      });
      window.location.href = url;
    } catch {
      setError(t("license.checkoutError"));
      setUpgrading(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  if (!license) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <PageHeader icon={CreditCard} title={t("license.title")} subtitle={t("license.subtitle")} secondaryGlow={false} />
        <div className="rounded-md border border-dashed p-8 text-center">
          <p className="text-muted-foreground">
            {t("license.noActive")}
          </p>
        </div>
        {canTrial && (
          <>
            {trialError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                {trialError}
              </div>
            )}
            <Button onClick={handleStartTrial} disabled={trialing} size="lg">
              {trialing ? t("license.startingTrial") : t("license.startTrialButton")}
            </Button>
          </>
        )}
      </div>
    );
  }

  const isExpiringSoon =
    new Date(license.expires_at).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        icon={CreditCard}
        title={t("license.title")}
        subtitle={t("license.subtitle")}
        secondaryGlow={false}
        titleBadge={
          license.plan === "community" ? undefined : (
            <Badge variant={license.status === "active" ? "default" : isTrial ? "outline" : "secondary"}>
              {isTrial ? t("license.badge.trial") : license.status}
            </Badge>
          )
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("license.cards.plan")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold capitalize">{license.plan}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("license.cards.seats")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {license.seats_used}{" "}
              <span className="text-base font-normal text-muted-foreground">
                / {license.seats}
              </span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("license.cards.features")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {license.features.map((f) => (
                <Badge key={f} variant="outline">
                  {f}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("license.cards.expires")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className={`text-2xl font-bold ${isExpiringSoon ? "text-destructive" : ""}`}
            >
              {formatDate(license.expires_at)}
            </p>
            {license.canceled_at && (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                {t("license.cards.canceledAt", { date: formatDate(license.canceled_at) })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("license.comparison.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-2 text-left font-medium text-muted-foreground">
                  {t("license.comparison.feature")}
                </th>
                <th className="pb-2 text-center font-medium text-muted-foreground">
                  {t("license.comparison.free")}
                </th>
                <th className="pb-2 text-center font-medium text-muted-foreground">
                  {t("license.comparison.pro")}
                </th>
              </tr>
            </thead>
            <tbody>
              {PLAN_FEATURES.map((f) => (
                <tr key={f.label} className="border-b last:border-0">
                  <td className="py-2">{f.label}</td>
                  <td className="py-2 text-center">
                    <div className="flex justify-center">
                      <FeatureCell value={f.free} />
                    </div>
                  </td>
                  <td className="py-2 text-center">
                    <div className="flex justify-center">
                      <FeatureCell value={f.pro} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      {trialError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {trialError}
        </div>
      )}

      {isExpired && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t("license.badge.expired", { date: formatDate(license.expires_at) })}
        </div>
      )}

      {isPartnerManaged ? (
        <div
          role="status"
          className="rounded-md border border-blue-300 bg-blue-500/10 px-4 py-3 text-sm text-blue-700 dark:border-blue-700 dark:text-blue-300"
        >
          {t("license.managedByPartner", { partner: license.grantor_partner_id })}
        </div>
      ) : canTrial ? (
        <Button onClick={handleStartTrial} disabled={trialing} size="lg">
          {trialing ? t("license.startingTrial") : t("license.startTrialButton")}
        </Button>
      ) : canUpgrade ? (
        <div className="space-y-3">
          <Tabs
            value={billingPeriod}
            onValueChange={(v) => setBillingPeriod(v as "monthly" | "annual")}
          >
            <TabsList>
              <TabsTrigger value="monthly">{t("license.period.monthly")}</TabsTrigger>
              <TabsTrigger value="annual">{t("license.period.annual")}</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => handleUpgrade()} disabled={upgrading} size="lg">
              {upgrading ? t("license.redirecting") : t("license.upgradeButton")}
            </Button>
            {billingPeriod === "annual" && !invoiceUrl && (
              <Button
                variant="outline"
                onClick={handleInvoice}
                disabled={invoicing}
                size="lg"
              >
                {invoicing ? t("license.invoicing") : t("license.invoiceButton")}
              </Button>
            )}
          </div>
          {invoiceUrl && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {t("license.invoiceSuccess")}{" "}
              <a
                href={invoiceUrl}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                View invoice
              </a>
            </div>
          )}
          {invoiceError && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              role="alert"
            >
              {invoiceError}
            </div>
          )}
        </div>
      ) : canManage || canChangeToAnnual ? (
        <div className="flex flex-wrap gap-3">
          {canChangeToAnnual && (
            <Button onClick={() => handleUpgrade("annual")} disabled={upgrading} size="lg">
              {upgrading ? t("license.redirecting") : t("license.changeToAnnualButton")}
            </Button>
          )}
          {canManage && (
            <Button onClick={handleManageSubscription} disabled={managing} variant="outline" size="lg">
              {managing ? t("license.managingSubscription") : t("license.manageSubscriptionButton")}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {isAdmin
            ? t("license.onProPlan")
            : t("license.contactAdmin")}
        </p>
      )}
    </div>
  );
}
